import { useEffect, useState } from 'react';
import type { DataTableRow, DataTableStateChangeEvent } from '../DataTable.model';
import { DataTableEvent } from '../DataTable.model';
import { useDataTable } from '../DataTableProvider';

/**
 * Hook that returns reactive rows array that updates when rows change.
 * Subscribes to TableStateChange events to trigger re-renders when rows are added/removed/updated.
 *
 * @returns Array of DataTable rows
 */
export const useRows = (): ReadonlyArray<DataTableRow> => {
  const { dataTableService } = useDataTable();
  const [rows, setRows] = useState<ReadonlyArray<DataTableRow>>(() => dataTableService.rows);

  useEffect(() => {
    const abortController = new AbortController();

    const stateChangeListener = (event: DataTableStateChangeEvent) => {
      if (event.detail.type.has('rows')) {
        setRows(dataTableService.rows);
      }
    };

    dataTableService.subscribe(DataTableEvent.TableStateChange, stateChangeListener, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [dataTableService]);

  return rows;
};
